import { reactive } from 'vue'
import { logStore } from './logTracker'
import { summarize } from './redact'

// Captura de trafico HTTP en dispositivo, para depurar builds donde no hay DevTools
// (release instalado desde Play: `webContentsDebuggingEnabled` esta apagado y
// `drop_console` borro los console.* del bundle).
//
// Se alimenta de interceptores de axios (toda la app pega por la instancia default
// de axios) mas un wrapper de `fetch` para lo poco que no pasa por ahi.
//
// Reglas de diseno, por si esto se toca despues:
//   - Memoria acotada: ring buffer de MAX_ENTRIES y bodies resumidos, nunca completos.
//     Los payloads de ocurrencias traen base64 de fotos; guardarlos crudos revienta el
//     dispositivo justo en el flujo que queremos observar.
//   - Cero cambio de comportamiento: los interceptores devuelven el mismo objeto que
//     recibieron y re-rechazan el mismo error. Del lado de `fetch` NO se lee el body
//     (un Response solo se consume una vez, leerlo aqui romperia al llamador).
//   - La entrada se crea al disparar la peticion (pending), asi una request que nunca
//     vuelve queda visible como colgada en vez de no aparecer.
//   - APAGADO por default: solo captura mientras `logStore.enabled` este activo, o sea
//     despues del gesto de los 7 taps. Un usuario normal en produccion no paga nada:
//     los interceptores quedan instalados pero salen en la primera linea. Sin esto el
//     gesto solo controlaba la UI y la captura corria para la flota entera.
//   - Nada sensible entra al buffer: ver AUTH_URL_PATTERN abajo y la politica
//     compartida de src/utils/redact.js (REDACTED_KEYS, summarize).

const MAX_ENTRIES = 120
const REDACTED_HEADERS = new Set(['authorization', 'cookie', 'set-cookie', 'proxy-authorization'])

export const netStore = reactive({
  entries: [],
  paused: false
})

let counter = 0
let installed = false

// Unica compuerta de la captura. `logStore.enabled` es el mismo flag que abre el
// LogTracker con los 7 taps, asi que el tracker no existe hasta que alguien lo pide
// a proposito: en un binario de produccion normal esto devuelve false siempre.
function capturing () {
  return logStore.enabled && !netStore.paused
}

function describeBody (data) {
  if (data === null || data === undefined || data === '') return null
  try {
    return JSON.stringify(summarize(data), null, 2)
  } catch (error) {
    return `[no serializable: ${error.message}]`
  }
}

// Segunda linea de defensa, por URL y no por nombre de clave: en los endpoints de
// autenticacion no se guarda NADA del body. Cubre el caso de un body que llegue como
// string ya serializado o urlencoded, donde la redaccion por clave no aplica.
const AUTH_URL_PATTERN = /(oauth|\/token|authenticate|login|password)/i

function bodyFor (url, data) {
  if (AUTH_URL_PATTERN.test(url || '')) return '[redactado: endpoint de autenticacion]'
  return describeBody(data)
}

// Presupuesto de nodos al medir un objeto. Corta el recorrido en payloads
// patologicos en vez de colgar la UI.
const SIZE_NODE_BUDGET = 200000

/**
 * Peso aproximado del payload en bytes. El peso es justo el dato que interesa
 * cuando algo falla en release y no en debug (413, timeouts, proxy), pero NO se
 * mide con JSON.stringify a proposito: los POST de ocurrencias traen base64 de
 * fotos y asignar un string de varios MB en un handheld de gama baja es lo que
 * dispara al low memory killer (el mismo motivo del foreground service del repo).
 * Este recorrido suma tamanos sin asignar nada.
 */
function approxByteLength (data) {
  if (data === null || data === undefined) return null
  if (typeof data === 'string') return data.length
  if (typeof Blob !== 'undefined' && data instanceof Blob) return data.size
  if (typeof ArrayBuffer !== 'undefined' && data instanceof ArrayBuffer) return data.byteLength
  if (typeof data === 'number' || typeof data === 'boolean') return String(data).length

  if (typeof FormData !== 'undefined' && data instanceof FormData) {
    let total = 0
    try {
      for (const [key, item] of data.entries()) {
        total += key.length
        if (typeof item === 'string') total += item.length
        else if (item && typeof item.size === 'number') total += item.size
      }
    } catch {
      return null
    }
    return total
  }

  if (typeof data !== 'object') return null

  let total = 0
  let budget = SIZE_NODE_BUDGET
  const stack = [data]
  const seen = new Set()

  while (stack.length) {
    if (budget-- <= 0) break
    const node = stack.pop()

    if (node === null || node === undefined) { total += 4; continue }
    const type = typeof node
    if (type === 'string') { total += node.length + 2; continue }
    if (type === 'number' || type === 'boolean') { total += String(node).length; continue }
    if (type !== 'object') continue

    if (seen.has(node)) continue
    seen.add(node)

    if (typeof Blob !== 'undefined' && node instanceof Blob) { total += node.size; continue }

    if (Array.isArray(node)) {
      total += node.length + 2
      for (const item of node) stack.push(item)
      continue
    }
    for (const key of Object.keys(node)) {
      total += key.length + 4
      stack.push(node[key])
    }
  }

  return total
}

function safeHeaders (headers) {
  if (!headers) return null
  const out = {}
  // Los headers de axios pueden ser AxiosHeaders (con toJSON) o un objeto plano.
  const plain = typeof headers.toJSON === 'function' ? headers.toJSON() : headers
  for (const key of Object.keys(plain)) {
    const value = plain[key]
    if (value === undefined || value === null || typeof value === 'object') continue
    out[key] = REDACTED_HEADERS.has(key.toLowerCase())
      ? `[presente, ${String(value).length} chars]`
      : String(value)
  }
  return Object.keys(out).length ? out : null
}

function stamp () {
  const d = new Date()
  const pad = (n, width = 2) => String(n).padStart(width, '0')
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`
}

function now () {
  return typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now()
}

function shortenUrl (url) {
  if (!url) return ''
  try {
    const parsed = new URL(url, 'https://placeholder.invalid')
    return parsed.pathname + (parsed.search || '')
  } catch {
    return url
  }
}

function pushEntry (entry) {
  netStore.entries.push(entry)
  if (netStore.entries.length > MAX_ENTRIES) netStore.entries.shift()
  return entry
}

function openEntry ({ source, method, url, reqHeaders, reqBody, reqBytes }) {
  return pushEntry({
    id: ++counter,
    source,
    time: stamp(),
    startedAt: now(),
    method: (method || 'GET').toUpperCase(),
    url,
    shortUrl: shortenUrl(url),
    online: typeof navigator !== 'undefined' ? navigator.onLine : null,
    pending: true,
    status: null,
    statusText: '',
    durationMs: null,
    reqHeaders,
    reqBody,
    reqBytes,
    resHeaders: null,
    resBody: null,
    resBytes: null,
    error: null
  })
}

function closeEntry (entry, patch) {
  if (!entry) return
  entry.pending = false
  entry.durationMs = Math.round(now() - entry.startedAt)
  Object.assign(entry, patch)
}

function entryById (id) {
  if (!id) return null
  return netStore.entries.find(item => item.id === id) || null
}

/**
 * Instala los interceptores sobre la instancia de axios recibida. Idempotente.
 * Debe llamarse ANTES de registrar el resto de interceptores en boot/axios.js:
 * axios corre los de request en orden inverso al de registro, asi que el primero
 * registrado es el ultimo en correr y ve el config final (ya con Authorization).
 * En response corre en orden de registro, asi que este ve el status crudo antes
 * de que responser lo transforme.
 */
export function installNetworkTracker (http) {
  if (installed) return
  installed = true

  http.interceptors.request.use(config => {
    if (!capturing()) return config
    try {
      const entry = openEntry({
        source: 'axios',
        method: config.method,
        url: config.url,
        reqHeaders: safeHeaders(config.headers),
        reqBody: bodyFor(config.url, config.data),
        reqBytes: approxByteLength(config.data)
      })
      config.__netTrackerId = entry.id
    } catch {
      // Nunca romper una peticion real por fallar al registrarla.
    }
    return config
  })

  http.interceptors.response.use(
    response => {
      try {
        closeEntry(entryById(response.config?.__netTrackerId), {
          status: response.status,
          statusText: response.statusText || '',
          resHeaders: safeHeaders(response.headers),
          resBody: bodyFor(response.config?.url, response.data),
          resBytes: approxByteLength(response.data)
        })
      } catch { /* ignorado a proposito */ }
      return response
    },
    error => {
      try {
        const config = error.config || error.response?.config
        const entry = entryById(config?.__netTrackerId)
        const response = error.response
        closeEntry(entry, {
          status: response ? response.status : null,
          statusText: response ? (response.statusText || '') : (error.code || 'sin respuesta'),
          resHeaders: response ? safeHeaders(response.headers) : null,
          resBody: response ? bodyFor(config?.url, response.data) : null,
          resBytes: response ? approxByteLength(response.data) : null,
          error: error.message || String(error)
        })
      } catch { /* ignorado a proposito */ }
      return Promise.reject(error)
    }
  )

  installFetchTracker()
}

/**
 * Wrapper de `fetch`. Solo mide: no toca el body del Response porque un stream
 * solo se consume una vez y leerlo aqui romperia al llamador.
 * Se ignoran `data:` y `blob:`, que en este proyecto son la mayoria de los fetch
 * (decodificar base64 de fotos) y no son trafico de red.
 */
function installFetchTracker () {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return
  const original = window.fetch

  window.fetch = function (resource, init) {
    let url = ''
    try {
      url = typeof resource === 'string' ? resource : (resource?.url || '')
    } catch { /* ignorado a proposito */ }

    const isLocal = /^(data|blob|filesystem):/i.test(url)
    if (!capturing() || isLocal || !url) return original.call(this, resource, init)

    let entry = null
    try {
      entry = openEntry({
        source: 'fetch',
        method: init?.method || resource?.method || 'GET',
        url,
        reqHeaders: init?.headers ? safeHeaders(init.headers) : null,
        reqBody: init ? bodyFor(url, init.body) : null,
        reqBytes: init ? approxByteLength(init.body) : null
      })
    } catch { /* ignorado a proposito */ }

    return original.call(this, resource, init).then(
      response => {
        closeEntry(entry, {
          status: response.status,
          statusText: response.statusText || '',
          resBody: '[body no leido: fetch se consume una sola vez]'
        })
        return response
      },
      error => {
        closeEntry(entry, {
          statusText: error.name === 'AbortError' ? 'abortada' : 'sin respuesta',
          error: error.message || String(error)
        })
        throw error
      }
    )
  }
}

export function clearRequests () {
  netStore.entries.splice(0, netStore.entries.length)
}

export function formatRequest (entry) {
  const status = entry.pending ? 'PENDIENTE' : entry.status || 'ERR'
  const took = entry.durationMs !== null ? `${entry.durationMs}ms` : ''
  const lines = [
    `${entry.time}  ${entry.method} ${status} ${took}`,
    `URL: ${entry.url}`,
    `origen: ${entry.source} | navigator.onLine al salir: ${entry.online}`
  ]
  if (entry.reqBytes !== null) lines.push(`payload: ${entry.reqBytes} bytes`)
  if (entry.resBytes !== null && entry.resBytes !== undefined) lines.push(`respuesta: ${entry.resBytes} bytes`)
  if (entry.error) lines.push(`error: ${entry.error}`)
  if (entry.reqHeaders) lines.push(`req headers: ${JSON.stringify(entry.reqHeaders)}`)
  if (entry.reqBody) lines.push(`req body:\n${entry.reqBody}`)
  if (entry.resHeaders) lines.push(`res headers: ${JSON.stringify(entry.resHeaders)}`)
  if (entry.resBody) lines.push(`res body:\n${entry.resBody}`)
  return lines.join('\n')
}
